/* eslint-disable @typescript-eslint/no-explicit-any */
import { act, useState } from 'react';
import { Badge, Checkbox, CheckboxOnChangeData, Link, makeStyles, Text, tokens, Tooltip } from '@fluentui/react-components';
import {
    bundleIcon,
    ChevronDoubleDownFilled,
    ChevronDoubleDownRegular,
    ChevronDoubleUpFilled,
    ChevronDoubleUpRegular,
    DeleteFilled,
    DeleteRegular,
    EditFilled,
    EditRegular,
    Note20Regular,
} from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { CaseEvent, CaseMilestone, CaseStatus, Note, Task, TaskStatus, UserAccount } from '../../../../data/types';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useUtilities } from '../../../../utilities/useUtilities';
import {
    CustomDataTable,
    CustomDataTableAction,
    CustomDataTableColumn,
    CustomDataTableProps,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { Row } from '../../../common';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { CaseEventService, CaseNoteService, CaseTaskService } from '../../../../data/services';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { AddEditMilestoneDialog } from '../../../dialogs/AddEditMilestoneDialog';
import { CaseTaskNotesDialog } from '../CaseTasks/CaseTaskNotesDialog';

const useStyles = makeStyles({
    taskText: {
        padding: '0px',
        margin: '0',
        overflow: 'hidden',
        width: 'inherit',
        display: 'block',
        overflowWrap: 'break-word',
        maxWidth: '100%',
    },
    taskTextWordBreak: {
        wordBreak: 'break-all',
    },
    completeBy: {
        color: tokens.colorBrandForeground1,
        pointerEvents: 'none',
        userSelect: 'none',
    },
    completeByOverdue: {
        color: tokens.colorStatusDangerForeground1,
        fontWeight: tokens.fontWeightBold,
    },
});

enum ColumnName {
    name = 'name',
    descrip = 'description',
    dueDate = 'dueDate',
    status = 'status',
    numTasks = 'numTasks',
    events = 'events',
    notes = 'notes',
}

type CaseMilestonesTableProps = Partial<CustomDataTableProps<Task>> & {
    milestones: CaseMilestone[];
    taskService: CaseTaskService;
    eventService: CaseEventService;
    noteService?: CaseNoteService;
    onSubmitMilestone?: (milestone: CaseMilestone) => Promise<void>;
    onMoveUp?: (milestone: CaseMilestone) => Promise<void>;
    onMoveDown?: (milestone: CaseMilestone) => Promise<void>;
    onDeleteMilestone?: (milestone: CaseMilestone) => Promise<void>;
    changeCaseStatusFromMilestone?: (status: CaseStatus) => void;
    onReopen?: (ms: CaseMilestone) => Promise<void>;
};
export const CaseMilestonesTable = (props: CaseMilestonesTableProps) => {
    const {
        milestones,
        taskService,
        eventService,
        noteService,
        onSubmitMilestone,
        onMoveUp,
        onMoveDown,
        onDeleteMilestone,
        changeCaseStatusFromMilestone,
        onReopen,
        bodyHeight,
        ...otherProps
    } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const utils = useUtilities();

    //State
    const [delConfirmOpen, setDelConfirmOpen] = useState<boolean>(false);
    const [editMilestoneOpen, setEditMilestoneOpen] = useState<boolean>(false);
    const [actionMilestone, setActionMilestone] = useState<CaseMilestone>();
    const [closeTasksOnRemove, setCloseTasksOnRemove] = useState<boolean>(true);
    const [deleteEventsOnRemove, setDeleteEventsOnRemove] = useState<boolean>(true);

    const [notesDialogOpen, setNotesDialogOpen] = useState<boolean>(false);
    const [actionMilestoneNotes, setActionMilestoneNotes] = useState<Note[]>();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Filters
    const filterTasksForMilestone = (milestone?: CaseMilestone) => (task: Task) => {
        return task.milestoneId == milestone?.milestoneId;
    };
    const filterOpenTasks = (task: Task) => {
        return task.status == TaskStatus.open;
    };
    const filterEventsForMilestone = (milestone?: CaseMilestone) => (event: CaseEvent) => {
        return event.milestoneId == milestone?.milestoneId;
    };

    //Logic
    const clickMoveUp = (milestone: CaseMilestone) => {
        !!onMoveUp && onMoveUp(milestone);
    };
    const clickMoveDown = (milestone: CaseMilestone) => {
        !!onMoveDown && onMoveDown(milestone);
    };
    const clickRemove = async (milestone: CaseMilestone | undefined) => {
        if (!!closeTasksOnRemove) {
            //close the remaining open tasks for this milestone
            const msTasks = taskService?.tasks?.filter(filterTasksForMilestone(actionMilestone)).filter(filterOpenTasks);

            try {
                await Promise.all(
                    msTasks.map((task: Task) => {
                        const editedTask = { ...task, milestoneId: '' };
                        return taskService.onClose(editedTask);
                    }),
                );
            } catch (error) {
                console.log(error);
            }
        }

        if (!!deleteEventsOnRemove) {
            //delete the events for this milestone
            const msEvents = eventService.events.filter(filterEventsForMilestone(actionMilestone));

            try {
                await Promise.all(
                    msEvents.map((event: CaseEvent) => {
                        return eventService.deleteCaseEventAsync(event.eventId!, event.caseId);
                    }),
                );
            } catch (error) {
                console.log(error);
            }
        }

        !!onDeleteMilestone && onDeleteMilestone(milestone!);
    };

    const onCloseTasksOnRemoveChange = (e: any, data: CheckboxOnChangeData) => {
        e?.stopPropagation?.();
        setCloseTasksOnRemove(!!data.checked);
    };
    const onDeleteEventsOnRemoveChange = (e: any, data: CheckboxOnChangeData) => {
        e?.stopPropagation?.();
        setDeleteEventsOnRemove(!!data.checked);
    };
    const onRemoveCheckboxClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
    };

    const getLowestSortKey = () => {
        const sortKeys = milestones?.map((m) => m.sortKey || 0) || [0];
        return Math.min(...sortKeys);
    };
    const getHighestSortKey = () => {
        const sortKeys = milestones?.map((m) => m.sortKey || 0) || [0];
        return Math.max(...sortKeys);
    };

    //Table Setup
    const columns: CustomDataTableColumn<CaseMilestone>[] = [
        {
            id: ColumnName.name,
            header: 'Milestone Name',
            renderCell: (milestone) => {
                return (
                    <>
                        <Link
                            className={commonStyles.button.root}
                            onClick={() => {
                                setActionMilestone(milestone);
                                setEditMilestoneOpen(true);
                            }}
                        >
                            <Text>{milestone.name}</Text>
                        </Link>
                    </>
                );
            },
            pointerEvents: true,
            defaultWidth: 200,
            minWidth: 100,
        },
        {
            id: ColumnName.descrip,
            header: 'Description',
            renderCell: (milestone) => {
                return (
                    <>
                        <Text>{milestone?.description}</Text>
                    </>
                );
            },
            defaultWidth: 450,
            minWidth: 200,
        },
        {
            id: ColumnName.dueDate,
            header: 'Due date',
            renderCell: (milestone) => {
                const epoch = new Date('1970-01-01T00:00:00Z');
                const hasDueDate = () => {
                    const compDate = new Date(milestone?.dueDate as Date);
                    return !!milestone?.dueDate && compDate > epoch;
                };
                const getDueDate = () => {
                    if (!!hasDueDate()) {
                        const dueDate = new Date(`${milestone?.dueDate as Date}`);
                        return `${utils.date.formatDate(dueDate, { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' }, true)}`;
                    }
                };
                return (
                    <>
                        {hasDueDate() && (
                            <>
                                <Text weight='semibold' className={styles.completeBy}>
                                    {getDueDate()}
                                </Text>
                            </>
                        )}
                    </>
                );
            },
            defaultWidth: 125,
            minWidth: 100,
        },
        {
            id: ColumnName.status,
            header: 'Status',
            renderCell: (milestone) => {
                const epoch = new Date('1970-01-01T00:00:00Z');
                const hasDueDate = () => {
                    const compDate = new Date(milestone?.dueDate as Date);
                    return !!milestone?.dueDate && compDate > epoch;
                };
                const isOverdue = () => {
                    const today = new Date();
                    return !!hasDueDate() && today > new Date(milestone?.dueDate as Date) && !milestone.completed;
                };
                const sortMilestones = (a: CaseMilestone, b: CaseMilestone) => {
                    return (a.sortKey || 0) - (b.sortKey || 0);
                };
                const isCurrent = () => {
                    const incompleteMilestones = milestones?.filter((ms) => !ms.completed).sort(sortMilestones);
                    return incompleteMilestones?.[0]?.milestoneId == milestone.milestoneId;
                };
                const hasOpenTasks = () => {
                    const tasks = taskService.tasks.filter((t) => t.milestoneId == milestone.milestoneId);
                    return tasks.some((t) => t.status != TaskStatus.completed && t.status != TaskStatus.closed);
                };
                const filterEventsForMilestone = (event: CaseEvent) => {
                    return event.milestoneId == milestone.milestoneId;
                };
                const readyToComplete = () => {
                    return (
                        isCurrent() &&
                        !milestone.completed &&
                        !milestone.blocked &&
                        milestone.deliverablesDelivered &&
                        taskService.tasks.filter(filterTasksForMilestone(milestone)).filter(filterOpenTasks).length == 0 &&
                        eventService.currentEvents.filter(filterEventsForMilestone).length == 0
                    );
                };
                return (
                    <>
                        <Row gap='small'>
                            {!!milestone.completed && (
                                <>
                                    <Badge appearance='tint' size='medium' color='success'>
                                        Complete
                                    </Badge>
                                    {hasOpenTasks() && (
                                        <Badge appearance='filled' size='medium' color='danger'>
                                            Open Tasks
                                        </Badge>
                                    )}
                                </>
                            )}
                            {isCurrent() && (
                                <Badge appearance='tint' size='medium' color='brand'>
                                    In Progress
                                </Badge>
                            )}
                            {isOverdue() && (
                                <Badge appearance='filled' size='medium' color='danger'>
                                    Overdue
                                </Badge>
                            )}
                            {readyToComplete() && (
                                <Badge appearance='tint' size='medium' color='success'>
                                    Ready to Complete
                                </Badge>
                            )}
                            {!!milestone.blocked && (
                                <Badge appearance='filled' size='medium' color='danger'>
                                    Blocked
                                </Badge>
                            )}
                        </Row>
                    </>
                );
            },
            defaultWidth: 120,
            minWidth: 100,
        },
        {
            id: ColumnName.numTasks,
            header: 'Remaining Tasks',
            renderCell: (milestone) => {
                const totalTasks = taskService.tasks.filter((t) => t.milestoneId == milestone.milestoneId).length;
                const remainingTasks =
                    totalTasks -
                    taskService.tasks.filter(
                        (t) =>
                            t.milestoneId == milestone.milestoneId &&
                            (t.status == TaskStatus.completed || t.status == TaskStatus.closed),
                    ).length;
                return (
                    <>
                        <Text>
                            {remainingTasks} / {totalTasks}
                        </Text>
                    </>
                );
            },
            justifyContent: 'center',
            defaultWidth: 100,
            minWidth: 100,
        },
        {
            id: ColumnName.events,
            header: 'Upcoming Events',
            renderCell: (milestone) => {
                const remainingEvents = eventService.events.filter(
                    (e) => e.milestoneId == milestone.milestoneId && new Date(e.date!) >= new Date(),
                );
                return (
                    <>
                        <Text>{remainingEvents.length}</Text>
                    </>
                );
            },
            justifyContent: 'center',
            defaultWidth: 100,
            minWidth: 100,
        },
        {
            id: ColumnName.notes,
            header: 'Notes',
            renderCell: (milestone) => {
                const milestoneNotes = noteService?.notes.filter((note) => note.milestoneId == milestone.milestoneId) || [];
                const notesCount = milestoneNotes.length;
                //alert('notesCount: ' + notesCount);
                return (
                    <>
                        {!!notesCount && (
                            <Tooltip content={`${notesCount} ${notesCount === 1 ? 'Note' : 'Notes'}`} relationship='label'>
                                <span
                                    onClick={() => {
                                        setActionMilestone(milestone);
                                        setActionMilestoneNotes(milestoneNotes);
                                        setNotesDialogOpen(true);
                                    }}
                                >
                                    <Note20Regular />
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            },
            justifyContent: 'center',
            defaultWidth: 50,
            minWidth: 50,
            pointerEvents: true,
        },
    ];

    //Icons
    const EditBundle = bundleIcon(EditFilled, EditRegular);
    const DeleteBundle = bundleIcon(DeleteFilled, DeleteRegular);
    const MoveUpBundle = bundleIcon(ChevronDoubleUpFilled, ChevronDoubleUpRegular);
    const MoveDownBundle = bundleIcon(ChevronDoubleDownFilled, ChevronDoubleDownRegular);

    const actions: CustomDataTableAction<CaseMilestone>[] = [
        {
            id: 'moveUp',
            ariaLabel: 'Move Up',
            canAccess: (milestone) => access.cases.canReorderMilestones(milestone?.caseId) || false,
            icon: <MoveUpBundle />,
            label: 'Move Up',
            tooltip: 'Move Up',
            onMenuClick: (milestone) => {
                clickMoveUp(milestone!);
            },
            onClick: (milestone) => {
                clickMoveUp(milestone!);
            },
            disabled: (milestone) => milestone?.sortKey == getLowestSortKey(),
        },
        {
            id: 'moveDown',
            ariaLabel: 'Move Down',
            canAccess: (milestone) => access.cases.canReorderMilestones(milestone?.caseId) || false,
            icon: <MoveDownBundle />,
            label: 'Move Down',
            tooltip: 'Move Down',
            onMenuClick: (milestone) => {
                clickMoveDown(milestone!);
            },
            onClick: (milestone) => {
                clickMoveDown(milestone!);
            },
            disabled: (milestone) => milestone?.sortKey == getHighestSortKey(),
        },
        {
            id: '',
            label: '',
            divider: true,
        },
        {
            id: 'edit',
            ariaLabel: 'Edit Milestone',
            canAccess: (milestone) => access.cases.canEditMilestones(milestone?.caseId) || false,
            icon: <EditBundle />,
            label: 'Edit Milestone',
            tooltip: 'Edit Milestone',
            onMenuClick: (milestone) => {
                setActionMilestone(milestone);
                setEditMilestoneOpen(true);
            },
            onClick: (milestone) => {
                setActionMilestone(milestone);
                setEditMilestoneOpen(true);
            },
        },
        {
            id: 'delete',
            ariaLabel: 'Delete',
            canAccess: (milestone) => access.cases.canDeleteMilestones(milestone?.caseId) || false,
            icon: <DeleteBundle />,
            label: 'Delete',
            tooltip: 'Delete',
            singleSelectItemOnClick: true,
            onMenuClick: (milestone) => {
                setActionMilestone(milestone);
                setDelConfirmOpen(true);
            },
            onClick: (milestone) => {
                setActionMilestone(milestone);
                setDelConfirmOpen(true);
            },
        },
    ];

    return (
        <>
            <CustomDataTable<CaseMilestone>
                items={milestones || []}
                rowId='milestoneId'
                bodyHeight={bodyHeight}
                columns={columns}
                actions={actions}
                select={false}
                {...otherProps}
            />
            <ConfirmationDialog
                title='Confirm Delete Milestone'
                text={`Are you sure you want to delete the "${actionMilestone?.name}" milestone?`}
                onConfirm={() => clickRemove(actionMilestone)}
                open={delConfirmOpen}
                onOpenChange={(isOpen) => {
                    setDelConfirmOpen(isOpen as boolean);
                    if (!isOpen) {
                        setActionMilestone(undefined);
                    }
                }}
                action={
                    <Row>
                        <Checkbox
                            checked={closeTasksOnRemove}
                            onChange={onCloseTasksOnRemoveChange}
                            onClick={onRemoveCheckboxClick}
                            label='Close open tasks?'
                            data-testid='case-milestones-close-open-tasks-checkbox'
                        />
                        <Checkbox
                            checked={deleteEventsOnRemove}
                            onChange={onDeleteEventsOnRemoveChange}
                            onClick={onRemoveCheckboxClick}
                            label='Delete current events?'
                            data-testid='case-milestones-delete-current-events-checkbox'
                        />
                    </Row>
                }
            />
            <AddEditMilestoneDialog
                submitMilestone={onSubmitMilestone}
                milestoneToEdit={actionMilestone}
                open={editMilestoneOpen}
                onOpenChange={(isOpen) => {
                    setEditMilestoneOpen(isOpen as boolean);
                    if (!isOpen) {
                        setActionMilestone(undefined);
                    }
                }}
                onChangeStatus={changeCaseStatusFromMilestone}
                onReopen={onReopen}
            />
            <CaseTaskNotesDialog
                title={`${actionMilestone?.name || 'Milestone Notes'}`}
                notes={actionMilestoneNotes}
                open={notesDialogOpen}
                onOpenChange={(isOpen) => {
                    setNotesDialogOpen(isOpen as boolean);
                    if (!isOpen) {
                        // setActionTask(undefined);
                        // setActionTaskNotes([]);
                    }
                }}
            />
        </>
    );
};
